import { createFileRoute } from '@tanstack/react-router';
import { Progress, ProgressBar } from '@osd/ui/progress';
import { Stack } from '@osd/ui/stack';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/progress — live docs for the `Progress` primitive.
 *
 * Two shapes:
 *   - Single value: <Progress value={42} variant="success" />
 *   - Multi-segment (stacked bars): render <ProgressBar> children
 *     inside <Progress>; the multi-segment form can't use the
 *     native <progress> element, so it falls back to role="progressbar".
 *
 * Plus `indeterminate` for unknown-progress shimmer (no animation
 * yet — see out-of-scope in #66).
 */
export const Route = createFileRoute('/ui/_layout/progress')({
  component: ProgressPage,
});

function ProgressPage() {
  return (
    <Stack gap={6}>
      <Section
        title="Progress"
        hint="Single value or stacked bars per variant (accent | success | warning | danger)"
      >
        <Stack gap={4}>
          <Progress value={42} ariaLabel="Single value (accent)" />
          <Progress value={80} variant="success" ariaLabel="Migrations" />
          <Progress value={30} variant="warning" ariaLabel="Disk quota" />
          <Progress indeterminate ariaLabel="Connecting" />
        </Stack>
      </Section>

      <Section title="Stacked segments" hint="Multi-bar Progress with mixed variants">
        <Stack gap={3}>
          <Progress ariaLabel="Storage breakdown">
            <ProgressBar value={30} variant="success" />
            <ProgressBar value={40} variant="warning" />
            <ProgressBar value={20} variant="danger" />
          </Progress>
        </Stack>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Single value"
            code={`<Progress value={42} ariaLabel="Migrations" />
`}
          />

          <CodeBlock
            language="tsx"
            title="Stacked segments"
            code={`<Progress ariaLabel="Storage breakdown">
  <ProgressBar value={30} variant="success" />
  <ProgressBar value={40} variant="warning" />
  <ProgressBar value={20} variant="danger" />
</Progress>
`}
          />

          <CodeBlock
            language="tsx"
            title="Indeterminate"
            code={`<Progress indeterminate ariaLabel="Connecting…" />
`}
          />
        </Stack>
      </Section>
    </Stack>
  );
}
